/**
 * Documents API Service
 * Handles document upload, staged imports, and transaction observations
 *
 * This service uses generated types from the OpenAPI spec for API responses,
 * and transforms them to frontend types (camelCase) for internal use.
 */
import api from './api';
import { getUploadMimeType } from '@/utils/uploadMimeType';
import type {
  StagedImport,
  ObservationsResponse,
  ConfirmAccountRequest,
  ConfirmAccountResponse,
  ResolveObservationRequest,
  UpdateObservationRequest,
  UpdateObservationCategoryRequest,
  ImportTransactionsRequest,
  ImportTransactionsResponse,
  Document,
  UploadFileResult,
  TransactionObservation,
  ObservationFilter,
  DetectedCategoriesResponse,
  CategoryDetection,
  ApplyCategoryMappingsRequest,
  ApplyCategoryMappingsResponse,
  SavedMappingsResponse,
  SavedCategoryMapping,
  CompetitorApp,
  UploadInitRequest,
  UploadInitResponse,
  UploadCompleteRequest,
  UploadCompleteResponse,
  ListDocumentsParams,
  ListDocumentsResponse,
  AccountResolution,
  DocumentCounts,
} from '@/types/document';

// Import generated types from OpenAPI spec for API response validation
import type { components } from '@/types/api.generated';

// Type aliases for generated API response types
type ApiObservationsWithContextResponse = components['schemas']['ObservationsWithContextResponse'];
type ApiTransactionObservationResponse = components['schemas']['TransactionObservationResponse'];
type ApiBalanceInfo = components['schemas']['BalanceInfo'];
type ApiDeduplicationSummary = components['schemas']['DeduplicationSummary'];

// ============================================================================
// Response Transformers
// ============================================================================

/**
 * Transform response for staged imports.
 * Backend now returns camelCase, but we still support snake_case for backwards compatibility.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformStagedImport(data: any): StagedImport {
  const parsedAccount = data.parsedAccount ?? data.parsed_account ?? {};
  return {
    id: data.id,
    documentId: data.documentId ?? data.document_id,
    status: data.status,
    parsedAccount: {
      institution: parsedAccount.institution ?? '',
      accountType: parsedAccount.accountType ?? parsedAccount.account_type ?? '',
      accountMask: parsedAccount.accountMask ?? parsedAccount.account_mask ?? '',
      statementStart: parsedAccount.statementStart ?? parsedAccount.statement_start ?? '',
      statementEnd: parsedAccount.statementEnd ?? parsedAccount.statement_end ?? '',
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    accountSuggestions: (data.accountSuggestions ?? data.account_suggestions ?? []).map((s: any) => ({
      accountId: s.accountId ?? s.account_id,
      accountName: s.accountName ?? s.account_name,
      accountMask: s.accountMask ?? s.account_mask,
      accountType: s.accountType ?? s.account_type,
      confidence: s.confidence,
      matchReasons: s.matchReasons ?? s.match_reasons ?? [],
    })),
    confirmedAccountId: data.confirmedAccountId ?? data.confirmed_account_id,
    observationCount: data.observationCount ?? data.observation_count ?? 0,
    isBalanceOnly: data.isBalanceOnly ?? data.is_balance_only ?? false,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

/**
 * Transform observation response from API.
 * Uses generated API type for type-safe transformation.
 * Backend now returns camelCase, but we still support snake_case for backwards compatibility.
 */
function transformObservationFromApi(data: ApiTransactionObservationResponse): TransactionObservation {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = data as any;
  // WP-DOC-30: Transform matched transaction details for duplicate review
  const matchedTxn = d.matchedTransaction ?? d.matched_transaction;
  return {
    id: d.id,
    stagedImportId: d.stagedImportId ?? d.staged_import_id,
    observedDate: d.observedDate ?? d.observed_date,
    observedAmount: Number(d.observedAmount ?? d.observed_amount),
    observedDescription: d.observedDescription ?? d.observed_description,
    observedBalance: (d.observedBalance ?? d.observed_balance) != null ? Number(d.observedBalance ?? d.observed_balance) : null,
    observedCategory: d.observedCategory ?? d.observed_category ?? null,
    matchStatus: (d.matchStatus ?? d.match_status) as TransactionObservation['matchStatus'],
    // WP-DOC-30: Include full matched transaction details for duplicate review modal
    matchedTransaction: matchedTxn
      ? {
          id: matchedTxn.id,
          date: matchedTxn.date,
          amount: Number(matchedTxn.amount),
          description: matchedTxn.description,
          source: matchedTxn.source,
        }
      : null,
    matchConfidence: d.matchConfidence ?? d.match_confidence ?? null,
    matchReasons: d.matchReasons ?? d.match_reasons ?? [],
    userAction: (d.userAction ?? d.user_action) as TransactionObservation['userAction'] ?? null,
    // Category suggestion fields (WP-DOC-26)
    suggestedCategoryId: d.suggestedCategoryId ?? d.suggested_category_id ?? null,
    suggestedCategoryName: d.suggestedCategoryName ?? d.suggested_category_name ?? null,
    categoryConfidence: d.categoryConfidence ?? d.category_confidence ?? null,
    // User override fields (WP-DOC-26)
    userCategoryId: d.userCategoryId ?? d.user_category_id ?? null,
    userCategoryName: d.userCategoryName ?? d.user_category_name ?? null,
    // Computed fields for display (WP-DOC-26)
    effectiveCategoryId: d.effectiveCategoryId ?? d.effective_category_id ?? null,
    effectiveCategoryName: d.effectiveCategoryName ?? d.effective_category_name ?? null,
    isUserOverride: d.isUserOverride ?? d.is_user_override ?? false,
  };
}

/**
 * Transform observation response.
 * Handles both snake_case and camelCase inputs for backwards compatibility.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformObservation(data: any): TransactionObservation {
  const matchedTxn = data.matchedTransaction ?? data.matched_transaction;
  return {
    id: data.id,
    stagedImportId: data.stagedImportId ?? data.staged_import_id,
    observedDate: data.observedDate ?? data.observed_date,
    observedAmount: Number(data.observedAmount ?? data.observed_amount ?? 0),
    observedDescription: data.observedDescription ?? data.observed_description ?? '',
    observedBalance: (data.observedBalance ?? data.observed_balance) != null
      ? Number(data.observedBalance ?? data.observed_balance)
      : null,
    observedCategory: data.observedCategory ?? data.observed_category ?? null,
    matchStatus: data.matchStatus ?? data.match_status ?? 'new',
    matchedTransaction: matchedTxn
      ? {
          id: matchedTxn.id,
          date: matchedTxn.date,
          amount: Number(matchedTxn.amount),
          description: matchedTxn.description,
          source: matchedTxn.source,
        }
      : null,
    matchConfidence: data.matchConfidence ?? data.match_confidence ?? null,
    matchReasons: data.matchReasons ?? data.match_reasons ?? [],
    userAction: data.userAction ?? data.user_action ?? null,
    // Category suggestion fields (WP-DOC-26)
    suggestedCategoryId: data.suggestedCategoryId ?? data.suggested_category_id ?? null,
    suggestedCategoryName: data.suggestedCategoryName ?? data.suggested_category_name ?? null,
    categoryConfidence: data.categoryConfidence ?? data.category_confidence ?? null,
    // User override fields (WP-DOC-26)
    userCategoryId: data.userCategoryId ?? data.user_category_id ?? null,
    userCategoryName: data.userCategoryName ?? data.user_category_name ?? null,
    // Computed fields for display (WP-DOC-26)
    effectiveCategoryId: data.effectiveCategoryId ?? data.effective_category_id ?? null,
    effectiveCategoryName: data.effectiveCategoryName ?? data.effective_category_name ?? null,
    isUserOverride: data.isUserOverride ?? data.is_user_override ?? false,
  };
}

/**
 * Transform API BalanceInfo to frontend BalanceInfo
 * Handles both camelCase and snake_case for backwards compatibility.
 */
function transformBalanceInfoFromApi(data: ApiBalanceInfo): ObservationsResponse['balanceInfo'] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = data as any;
  return {
    openingBalance: (d.openingBalance ?? d.opening_balance) != null ? Number(d.openingBalance ?? d.opening_balance) : null,
    closingBalance: (d.closingBalance ?? d.closing_balance) != null ? Number(d.closingBalance ?? d.closing_balance) : null,
    netChange: Number(d.netChange ?? d.net_change),
    statementClosing: (d.statementClosing ?? d.statement_closing) != null ? Number(d.statementClosing ?? d.statement_closing) : null,
    balanceMatches: d.balanceMatches ?? d.balance_matches,
  };
}

/**
 * Transform API DeduplicationSummary to frontend ObservationsSummary
 * Handles both camelCase and snake_case for backwards compatibility.
 */
function transformSummaryFromApi(data: ApiDeduplicationSummary): ObservationsResponse['summary'] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = data as any;
  return {
    new: d.new,
    duplicate: d.duplicate,
    needsReview: d.needsReview ?? d.needs_review,
  };
}

// ============================================================================
// Documents Service
// ============================================================================

export const documentsService = {
  // ==========================================================================
  // Document Upload
  // ==========================================================================

  /**
   * Upload a file and start its processing: upload/init, the pre-signed POST,
   * then upload/complete (WP-IMPORT-1).
   *
   * Replaces a "legacy direct upload" that posted multipart to /documents, a
   * route core does not have. Processing is asynchronous: the result names the
   * document to poll (useStagedImportByDocumentId) until its staged import is
   * ready for review.
   */
  uploadFile: async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<UploadFileResult> => {
    const init = await documentsService.initUpload({
      filename: file.name,
      mimeType: getUploadMimeType(file),
      sizeBytes: file.size,
    });
    const etag = await documentsService.uploadToS3(init.uploadUrl, init.uploadFields, file, onProgress);
    const complete = await documentsService.completeUpload({ documentId: init.documentId, etag });
    return { documentId: complete.document.id ?? init.documentId };
  },

  /**
   * Initialize a pre-signed URL upload (Step 1 of 3)
   * Returns a pre-signed S3 URL and form fields for direct upload
   */
  initUpload: async (request: UploadInitRequest): Promise<UploadInitResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>('/documents/upload/init', {
      filename: request.filename,
      mime_type: request.mimeType,
      size_bytes: request.sizeBytes,
      document_type: request.documentType,
      folder_path: request.folderPath,
    });

    return {
      documentId: response.document_id ?? response.documentId,
      uploadUrl: response.upload_url ?? response.uploadUrl,
      uploadFields: response.upload_fields ?? response.uploadFields ?? {},
      expiresAt: response.expires_at ?? response.expiresAt,
    };
  },

  /**
   * Upload file directly to S3 using pre-signed URL (Step 2 of 3)
   * This bypasses our API server and uploads directly to object storage
   * @param uploadUrl - The pre-signed S3 URL from initUpload
   * @param uploadFields - The form fields to include with the upload
   * @param file - The file to upload
   * @param onProgress - Optional progress callback (0-100)
   */
  uploadToS3: async (
    uploadUrl: string,
    uploadFields: Record<string, string>,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string | null> => {
    const formData = new FormData();

    // Add all upload fields first (order matters for S3)
    Object.entries(uploadFields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // File must be last
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Extract ETag from response headers if available
          const etag = xhr.getResponseHeader('ETag');
          resolve(etag);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed due to network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was cancelled'));
      });

      xhr.open('POST', uploadUrl);
      xhr.send(formData);
    });
  },

  /**
   * Complete the upload and trigger processing (Step 3 of 3)
   * Call this after successfully uploading to S3
   */
  completeUpload: async (request: UploadCompleteRequest): Promise<UploadCompleteResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>('/documents/upload/complete', {
      document_id: request.documentId,
      etag: request.etag,
    });

    return {
      document: {
        id: response.document?.id ?? response.id,
        filename: response.document?.filename ?? response.filename,
        mimeType: response.document?.mime_type ?? response.document?.mimeType ?? '',
        sizeBytes: response.document?.size_bytes ?? response.document?.sizeBytes ?? 0,
        status: response.document?.status ?? response.status,
        createdAt: response.document?.created_at ?? response.document?.createdAt,
      },
      processingStatus: response.processing_status ?? response.processingStatus ?? 'queued',
      taskId: response.task_id ?? response.taskId ?? '',
      stagedImportId: response.staged_import_id ?? response.stagedImportId ?? null,
    };
  },

  /**
   * Get document by ID
   */
  getDocument: async (id: string): Promise<Document> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(`/documents/${id}`);
    return {
      id: response.id,
      filename: response.filename,
      mimeType: response.mime_type ?? response.mimeType,
      sizeBytes: response.size_bytes ?? response.sizeBytes ?? 0,
      status: response.status,
      errorMessage: response.error_message ?? response.errorMessage,
      stagedImportId: response.staged_import_id ?? response.stagedImportId,
      createdAt: response.created_at ?? response.createdAt,
      updatedAt: response.updated_at ?? response.updatedAt,
    };
  },

  /**
   * Get list of documents with pagination and filters
   */
  getDocuments: async (params?: ListDocumentsParams): Promise<ListDocumentsResponse> => {
    const queryParams: Record<string, unknown> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.offset !== undefined) queryParams.offset = params.offset;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/documents', queryParams);

    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      documents: (response.documents ?? []).map((d: any) => ({
        id: d.id,
        filename: d.filename,
        mimeType: d.mime_type ?? d.mimeType,
        sizeBytes: d.size_bytes ?? d.sizeBytes ?? 0,
        status: d.status,
        errorMessage: d.error_message ?? d.errorMessage,
        stagedImportId: d.staged_import_id ?? d.stagedImportId,
        createdAt: d.created_at ?? d.createdAt,
        updatedAt: d.updated_at ?? d.updatedAt,
      })),
      total: response.total ?? 0,
      limit: response.limit ?? params?.limit ?? 20,
      offset: response.offset ?? params?.offset ?? 0,
    };
  },

  /**
   * Delete a document
   */
  deleteDocument: async (id: string): Promise<void> => {
    await api.delete(`/documents/${id}`);
  },

  /**
   * Retry processing a failed document
   */
  retryDocument: async (id: string): Promise<Document> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(`/documents/${id}/retry`);
    const doc = response.document ?? response;
    return {
      id: doc.id,
      filename: doc.filename,
      mimeType: doc.mime_type ?? doc.mimeType,
      sizeBytes: doc.size_bytes ?? doc.sizeBytes ?? 0,
      status: doc.status,
      errorMessage: doc.error_message ?? doc.errorMessage,
      stagedImportId: doc.staged_import_id ?? doc.stagedImportId,
      createdAt: doc.created_at ?? doc.createdAt,
      updatedAt: doc.updated_at ?? doc.updatedAt,
    };
  },

  /**
   * Get document counts by status for tab badges (WP-DOC-17)
   * Handle both camelCase (actual API) and snake_case (legacy) field names
   */
  getDocumentCounts: async (): Promise<DocumentCounts> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/documents/counts');
    return {
      processing: response.processing ?? 0,
      pending_review: response.pendingReview ?? response.pending_review ?? 0,
      completed: response.completed ?? 0,
      failed: response.failed ?? 0,
      all: response.all ?? 0,
    };
  },

  // ==========================================================================
  // Staged Imports
  // ==========================================================================

  /**
   * Get a staged import by ID
   */
  getStagedImport: async (id: string): Promise<StagedImport> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(`/staged-imports/${id}`);
    return transformStagedImport(response);
  },

  /**
   * Get staged import by document ID (WP-BUG-19)
   * Returns null if no staged import exists yet (document still processing)
   */
  getStagedImportByDocumentId: async (documentId: string): Promise<StagedImport | null> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/staged-imports', { document_id: documentId });
    const stagedImports = response.staged_imports ?? response.stagedImports ?? [];
    if (stagedImports.length === 0) {
      return null;
    }
    return transformStagedImport(stagedImports[0]);
  },

  /**
   * Get all staged imports for the current user
   */
  getStagedImports: async (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ stagedImports: StagedImport[]; total: number }> => {
    const queryParams: Record<string, unknown> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.offset !== undefined) queryParams.offset = params.offset;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/staged-imports', queryParams);

    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      stagedImports: (response.staged_imports ?? response.stagedImports ?? []).map((si: any) =>
        transformStagedImport(si)
      ),
      total: response.total ?? 0,
    };
  },

  /**
   * Confirm account mapping for a staged import
   */
  confirmAccount: async (
    stagedImportId: string,
    request: ConfirmAccountRequest
  ): Promise<ConfirmAccountResponse> => {
    // Transform request to snake_case
    const requestData: Record<string, unknown> = {};
    if (request.accountId) {
      requestData.account_id = request.accountId;
    }
    if (request.createAccount) {
      requestData.create_account = {
        institution: request.createAccount.institution,
        account_type: request.createAccount.accountType,
        account_name: request.createAccount.accountName,
        account_mask: request.createAccount.accountMask,
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(
      `/staged-imports/${stagedImportId}/confirm-account`,
      requestData
    );

    return {
      stagedImportId: response.staged_import_id ?? response.stagedImportId ?? stagedImportId,
      confirmedAccountId: response.confirmed_account_id ?? response.confirmedAccountId,
      status: response.status,
    };
  },

  /**
   * Resolve multiple accounts for a multi-account staged import
   * Supports link, create, and skip actions for each parsed account
   */
  resolveAccounts: async (
    stagedImportId: string,
    resolutions: AccountResolution[]
  ): Promise<{ success: boolean; resolvedCount: number; createdAccountIds: string[] }> => {
    // Transform resolutions to snake_case
    const requestData = {
      resolutions: resolutions.map((r) => ({
        parsed_account_id: r.parsedAccountId,
        action: r.action,
        linked_account_id: r.linkedAccountId,
        new_account_data: r.newAccountData
          ? {
              name: r.newAccountData.name,
              type: r.newAccountData.type,
              subtype: r.newAccountData.subtype,
              institution: r.newAccountData.institution,
              mask: r.newAccountData.mask,
            }
          : undefined,
      })),
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(
      `/staged-imports/${stagedImportId}/resolve-accounts`,
      requestData
    );

    return {
      success: response.success ?? true,
      resolvedCount: response.resolved_count ?? response.resolvedCount ?? resolutions.length,
      createdAccountIds: response.created_account_ids ?? response.createdAccountIds ?? [],
    };
  },

  // ==========================================================================
  // Transaction Observations
  // ==========================================================================

  /**
   * Get transaction observations for a staged import
   *
   * Uses generated types from OpenAPI spec (ApiObservationsWithContextResponse)
   * to ensure type safety with the backend API contract.
   */
  getObservations: async (
    stagedImportId: string,
    params?: {
      filter?: ObservationFilter;
      limit?: number;
      offset?: number;
    }
  ): Promise<ObservationsResponse> => {
    const queryParams: Record<string, unknown> = {};
    if (params?.filter && params.filter !== 'all') {
      queryParams.status = params.filter;
    }
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.offset !== undefined) queryParams.offset = params.offset;

    // Use generated API type for type-safe response handling
    // Note: The generated types use snake_case, but the API actually returns camelCase
    // due to Pydantic's serialize_by_alias=True. We cast to any to handle both cases
    // until the OpenAPI spec is regenerated with camelCase field names.
    const response = await api.get<ApiObservationsWithContextResponse>(
      `/staged-imports/${stagedImportId}/observations`,
      queryParams
    );

    // Transform API response to frontend types
    // Handle both camelCase (actual API response) and snake_case (legacy/generated types)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resp = response as any;
    return {
      observations: (resp.observations ?? []).map(transformObservationFromApi),
      total: resp.total,
      summary: transformSummaryFromApi(resp.summary),
      balanceInfo: transformBalanceInfoFromApi(resp.balanceInfo ?? resp.balance_info),
    };
  },

  /**
   * Resolve a duplicate decision for an observation
   */
  resolveObservation: async (
    observationId: string,
    request: ResolveObservationRequest
  ): Promise<TransactionObservation> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(`/observations/${observationId}/resolve`, {
      action: request.action,
      apply_to_similar: request.applyToSimilar,
    });

    return transformObservation(response);
  },

  /**
   * Update an observation (edit transaction details before import)
   */
  updateObservation: async (
    observationId: string,
    request: UpdateObservationRequest
  ): Promise<TransactionObservation> => {
    const requestData: Record<string, unknown> = {};
    if (request.observedDate !== undefined) requestData.observed_date = request.observedDate;
    if (request.observedAmount !== undefined) requestData.observed_amount = request.observedAmount;
    if (request.observedDescription !== undefined)
      requestData.observed_description = request.observedDescription;
    if (request.categoryId !== undefined) requestData.category_id = request.categoryId;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.patch<any>(`/observations/${observationId}`, requestData);

    return transformObservation(response);
  },

  /**
   * Update observation category (WP-DOC-26)
   * Sets user override category, or clears it (null reverts to LLM suggestion)
   */
  updateObservationCategory: async (
    stagedImportId: string,
    observationId: string,
    request: UpdateObservationCategoryRequest
  ): Promise<TransactionObservation> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.patch<any>(
      `/staged-imports/${stagedImportId}/observations/${observationId}/category`,
      { category_id: request.categoryId }
    );

    return transformObservation(response);
  },

  /**
   * Bulk resolve observations
   */
  bulkResolveObservations: async (
    observationIds: string[],
    action: 'import' | 'skip'
  ): Promise<{ resolved: number }> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>('/observations/bulk-resolve', {
      observation_ids: observationIds,
      action,
    });

    return {
      resolved: response.resolved ?? observationIds.length,
    };
  },

  // ==========================================================================
  // Import Operations
  // ==========================================================================

  /**
   * Import transactions from a staged import
   */
  importTransactions: async (
    stagedImportId: string,
    request: ImportTransactionsRequest
  ): Promise<ImportTransactionsResponse> => {
    const requestData: Record<string, unknown> = {};
    if (request.observationIds) requestData.observation_ids = request.observationIds;
    if (request.importAllNew !== undefined) requestData.import_all_new = request.importAllNew;
    if (request.skipDuplicates !== undefined) requestData.skip_duplicates = request.skipDuplicates;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(`/staged-imports/${stagedImportId}/import`, requestData);

    return {
      importedCount: response.imported_count ?? response.importedCount ?? 0,
      skippedCount: response.skipped_count ?? response.skippedCount ?? 0,
      anchorCreated: response.anchor_created ?? response.anchorCreated ?? false,
      transactionIds: response.transaction_ids ?? response.transactionIds ?? [],
    };
  },

  /**
   * Cancel a staged import
   */
  cancelStagedImport: async (stagedImportId: string): Promise<void> => {
    await api.delete(`/staged-imports/${stagedImportId}`);
  },

  // ==========================================================================
  // Category Mapping (Competitor Import)
  // ==========================================================================

  /**
   * Get detected categories for a staged import
   */
  getDetectedCategories: async (stagedImportId: string): Promise<DetectedCategoriesResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(
      `/category-mappings/staged-imports/${stagedImportId}/categories`
    );

    return {
      stagedImportId: response.staged_import_id ?? response.stagedImportId ?? stagedImportId,
      sourceApp: response.source_app ?? response.sourceApp ?? null,
      categories: (response.categories ?? []).map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (cat: any): CategoryDetection => ({
          sourceCategory: cat.source_category ?? cat.sourceCategory,
          transactionCount: cat.transaction_count ?? cat.transactionCount ?? 0,
          suggestedFlowioCategoryId:
            cat.suggested_flowio_category_id ?? cat.suggestedFlowioCategoryId ?? null,
          suggestedFlowioCategoryName:
            cat.suggested_flowio_category_name ?? cat.suggestedFlowioCategoryName ?? null,
          confidence: Number(cat.confidence ?? 0),
        })
      ),
      totalTransactions: response.total_transactions ?? response.totalTransactions ?? 0,
    };
  },

  /**
   * Apply category mappings to a staged import
   */
  applyCategoryMappings: async (
    stagedImportId: string,
    request: ApplyCategoryMappingsRequest
  ): Promise<ApplyCategoryMappingsResponse> => {
    const requestData = {
      source_app: request.sourceApp,
      mappings: request.mappings.map((m) => ({
        source_category: m.sourceCategory,
        flowio_category_id: m.flowioCategoryId,
        save_for_future: m.saveForFuture,
      })),
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(
      `/category-mappings/staged-imports/${stagedImportId}/categories`,
      requestData
    );

    return {
      success: response.success ?? true,
      transactionsUpdated: response.transactions_updated ?? response.transactionsUpdated ?? 0,
      mappingsSaved: response.mappings_saved ?? response.mappingsSaved ?? 0,
      message: response.message ?? 'Mappings applied successfully',
    };
  },

  /**
   * Get saved category mappings for reuse
   */
  getSavedMappings: async (sourceApp?: CompetitorApp): Promise<SavedMappingsResponse> => {
    const params: Record<string, unknown> = {};
    if (sourceApp) {
      params.source_app = sourceApp;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/category-mappings/saved', params);

    return {
      mappings: (response.mappings ?? []).map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (m: any): SavedCategoryMapping => ({
          id: m.id,
          sourceApp: m.source_app ?? m.sourceApp,
          sourceCategory: m.source_category ?? m.sourceCategory,
          sourceSubcategory: m.source_subcategory ?? m.sourceSubcategory ?? null,
          targetCategoryId: m.target_category_id ?? m.targetCategoryId ?? null,
          createdAt: m.created_at ?? m.createdAt,
        })
      ),
      total: response.total ?? 0,
    };
  },

  /**
   * Delete a saved category mapping
   */
  deleteSavedMapping: async (mappingId: string): Promise<void> => {
    await api.delete(`/category-mappings/saved/${mappingId}`);
  },
};

export default documentsService;
